import { inject, injectable } from 'inversify';
import Debug from 'debug';
import * as Api from '@xlearn/common';
import { QuizStoreModule, QuizStoreModuleSymbol, Quiz2 } from '../../state2';
import { ExamDetails, ExamProps, PracticeProps, PracticeDetails,
  QuizRole, QuestionReference, QuestionLevel } from '../../state2/quiz';
import { reportNonFatalError } from '@/app/error-handling';
import { MsgPackCourseFileLoader, MsgPackCourseFileLoaderSymbol } from '@/app/fetch/msgpack-course-file';
import { CancelToken } from '@/app/shared/utils';
import { replaceBasePathInContent } from '@/app/shared/utils/base-path';

const debug = Debug('load-quizzes-command');

export interface QuizSource {
  uri: string;
  basePath: string;
}

@injectable()
export class LoadQuizzesCommandHandler {

  constructor(
    @inject(QuizStoreModuleSymbol)
    private store: QuizStoreModule,
    @inject(MsgPackCourseFileLoaderSymbol)
    private fileLoader: MsgPackCourseFileLoader) {
  }

  public async execute(source: QuizSource, finalQuizId: string, cancelToken: CancelToken) {

    debug('loading quiz file', source);

    this.store.startLoading();

    const loadResult = await this.fileLoader.load<Api.SerializedQuiz[]>(source.uri, cancelToken)
    if (!loadResult.success) {
      return loadResult
    }
    
    const mapped = loadResult.value.map((x) => this.mapQuiz(x, finalQuizId, source.basePath));

    debug('quiz file loaded: %j', mapped);

    this.store.finishLoading(mapped);

    return Api.ok(true as true)
  }

  private mapQuiz(quiz: Api.SerializedQuiz, finalQuizId: string, basePath: string): Quiz2 {
    debug('mapping quiz', quiz)

    let examProps: ExamProps = { hasExam: false }
    if (quiz.allowedModes.indexOf(Api.QuizMode.exam) >= 0) {
      const details = this.mapExamDetails(quiz, basePath)
      examProps = {
        hasExam: true,
        exam: details
      }
    }

    let practiceProps: PracticeProps = { hasPractice: false }
    if (quiz.allowedModes.indexOf(Api.QuizMode.practice) >= 0) {
      const details = this.mapPracticeDetails(quiz, basePath)
      practiceProps = {
        hasPractice: true,
        practice: details
      }
    }

    const role = quiz.id === finalQuizId ? 'final' : 'section' as QuizRole
    
    const mapped: Quiz2 = {
      id: quiz.id,
      title: quiz.title,
      href: quiz.href,
      context: {
        parentTitle: quiz.context.parentTitle
      },
      role,
      ...examProps,
      ...practiceProps
    };

    debug('mapped quiz', mapped)

    return mapped
  }

  private mapQuestionReference(question: Api.SerializedQuestionReference): QuestionReference {

    return {
      questionId: question.questionId,
      level: mapLevel(question.level),
      minScore: question.minScore,
      maxScore: question.maxScore,
      topics: question.topics,
      competences: question.competences
    }

    function mapLevel(level: Api.QuestionLevel): QuestionLevel {
      switch (level) {
        case Api.QuestionLevel.easy:
          return 'easy'
        case Api.QuestionLevel.normal:
          return 'normal'
        case Api.QuestionLevel.hard:
          return 'hard'
      }
    }
  }

  private mapPracticeDetails(quiz: Api.SerializedQuiz, basePath: string): PracticeDetails {

    const descriptionAndFeedback = this.mapDescriptionAndFeedback(quiz.description.practice,
                                                                  quiz.feedbacks.practice,
                                                                  basePath)

    const questions = quiz.questions.practice ?
          quiz.questions.practice.map(x => this.mapQuestionReference(x)) : []

    return {
      ...descriptionAndFeedback,
      questionCount: quiz.options.questionCount,
      questions
    }
  }

  private mapExamDetails(quiz: Api.SerializedQuiz, basePath: string): ExamDetails {

    const examOptions = quiz.examOptions
    if (!examOptions) {
      reportNonFatalError(`Coding error: missing exam options of quiz with id ${quiz.id}`)
      throw new Error(`Coding error: missing exam options of quiz with id ${quiz.id}`)
    }

    const descriptionAndFeedback = this.mapDescriptionAndFeedback(quiz.description.exam,
                                                                  quiz.feedbacks.exam,
                                                                  basePath)

    const questions = quiz.questions.exam ?
          quiz.questions.exam.map(x => this.mapQuestionReference(x)) : []

    return {
            ...descriptionAndFeedback,
            questionCount: quiz.options.questionCount,
            questions,
            percentToPass: quiz.options.percentToPass,
            timeLimitSeconds: examOptions.timeLimitSeconds,
            attemptsAllowed: examOptions.attemptsAllowed,
            gradingMethod: mapGradingMethod(examOptions.gradingMethod),
            reviewOptions: {
              whetherCorrect: examOptions.reviewOptions.whetherCorrect,
              rightAnswer: examOptions.reviewOptions.rightAnswer
            }
    }

    function mapGradingMethod(method: Api.SerializedGradingMethod) {
      switch (method) {
        case Api.SerializedGradingMethod.highestGrade:
          return 'highest-grade'
        case Api.SerializedGradingMethod.lastAttempt:
          return 'last-attempt'
      }
    }
  }

  private mapDescriptionAndFeedback(description: string | undefined,
                                    feedback: Api.SerializedFeedback | undefined,
                                    basePath: string) {

    const replacePath = replaceBasePathInContent(basePath)

    return {
      description: description ? replacePath(description) : '',
      feedback: feedback ? {
        success: replacePath(feedback.success),
        fail: replacePath(feedback.fail)
      } : {
        success: '',
        fail: ''
      }
    }
  }
}
